import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { hot } from 'react-hot-loader';
import { compose } from 'recompose';

import * as ROUTES from '../../constants/routes';

import { withAuthentication } from '../../session/index';

import 'bootstrap/dist/css/bootstrap.min.css';

import UpdatePost from '../UpdatePost/index';
import Policy from '../Policy/index';
import AccountSettings from '../Account/index';
import StripeSuccessRedirect from '../StripeSuccessRedirect';
import PostsPreview from '../Posts/PostsPreview';
import MainLayout from '../../components/layout/Main';
import { withNotificationProvider } from '../../providers/NotificationProvider';
import { withPostProvider } from '../../providers/PostProvider';
import UnauthorizedLayout from '../../components/layout/Unauthorized';
import SignInPage from '../Signin';
import SignUpPage from '../Signup';
import ForgotPassword from '../ForgotPassword';
import MobileView from '../../components/mobileView';
import { useMedia } from 'react-use';
import PostCreation from '../PostCreation';
import InvitedPosts from '../InvitedPosts/InvitedPosts';
import PublishedPosts from '../PublishedPosts/PublishedPosts';
import { useAuth } from '../../hooks/useAuth';
import PendingPosts from '../PendingPosts/PendingPosts';
import ProjectPostPage from '../ProjectPostPage/ProjectPostPage';
import InvitationResult from '../InvitationResult/InvitationResult';
import AboutUs from '../AboutUs/AboutUs';
import Unsubscribe from '../Unsubscribe/Unsubscribe';
import ProtectedRoute from '../../helpers/ProtectedRoute';
import ProtectedUnauthorizedRoute from '../../helpers/ProtectedUnauthorizedRoute';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ReactGA from 'react-ga4';

ReactGA.initialize(process.env.REACT_APP_GA_PROJECT_ID);

const App = () => {
    const isMobile = useMedia('(max-width: 1000px)');
    const { authLoading, isGuest } = useAuth();

    const isAuth = !isGuest;
    const location = useLocation();

    useEffect(() => ReactGA.send(location.pathname + location.search), [location]);

    if (isMobile) {
        return <MobileView />;
    }

    return (
        <div>
            <Routes>
                <Route
                    exact
                    path={ROUTES.INVITATION_RESULT}
                    element={<InvitationResult isAuth={isAuth} />}
                />
                <Route exact path={ROUTES.PREVIEW} element={<PostsPreview />} />
                <Route exact path={ROUTES.ACCOUNT_SETTINGS} element={<AccountSettings />} />
                <Route exact path={ROUTES.POLICY} element={<Policy />} />
                <Route path={ROUTES.UNSUBSCRIBE}>
                    <Route index element={<Navigate to={ROUTES.LANDING} />} />
                    <Route path=":id" element={<Unsubscribe />} />
                </Route>
                <Route
                    path={ROUTES.LANDING}
                    element={
                        isAuth || (!isAuth && authLoading) ? <MainLayout /> : <UnauthorizedLayout />
                    }
                >
                    <Route
                        index
                        element={
                            <Navigate
                                to={
                                    isAuth || (!isAuth && authLoading)
                                        ? ROUTES.MY_ALL_POSTS
                                        : ROUTES.SIGN_IN
                                }
                            />
                        }
                    />
                    <Route path={ROUTES.USER_GUIDE} element={<AboutUs />} />
                    <Route path={ROUTES.POSTS}>
                        <Route index element={<Navigate to={ROUTES.MY_ALL_POSTS} />} />
                        <Route
                            exact
                            path={ROUTES.INVITED_POSTS}
                            element={
                                <ProtectedRoute>
                                    <InvitedPosts />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            exact
                            path={ROUTES.MY_ALL_POSTS}
                            element={
                                <ProtectedRoute>
                                    <PublishedPosts />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            exact
                            path={ROUTES.PENDING_POSTS}
                            element={
                                <ProtectedRoute>
                                    <PendingPosts />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path=":id"
                            element={
                                <ProtectedRoute>
                                    <ProjectPostPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            exact
                            path={ROUTES.CREATE_POST}
                            element={
                                <ProtectedRoute>
                                    <PostCreation />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            exact
                            path={ROUTES.UPDATE_POST}
                            element={
                                <ProtectedRoute>
                                    <UpdatePost />
                                </ProtectedRoute>
                            }
                        />
                    </Route>
                    <Route
                        path={ROUTES.PROFILE}
                        element={
                            <ProtectedRoute>
                                <PublishedPosts />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={ROUTES.SIGN_UP}
                        element={
                            <ProtectedUnauthorizedRoute>
                                <SignUpPage />
                            </ProtectedUnauthorizedRoute>
                        }
                    />
                    <Route
                        path={ROUTES.SIGN_IN}
                        element={
                            <ProtectedUnauthorizedRoute>
                                <SignInPage />
                            </ProtectedUnauthorizedRoute>
                        }
                    />
                    <Route
                        exact
                        path={ROUTES.RESET_PASSWORD}
                        element={
                            <ProtectedUnauthorizedRoute>
                                <ForgotPassword />
                            </ProtectedUnauthorizedRoute>
                        }
                    />
                    <Route
                        path="*"
                        element={
                            <Navigate
                                to={
                                    isAuth || (!isAuth && authLoading)
                                        ? ROUTES.MY_ALL_POSTS
                                        : ROUTES.SIGN_IN
                                }
                            />
                        }
                    />
                </Route>
                <Route path={ROUTES.STRIPE_SUCCESS_AUTH} element={<StripeSuccessRedirect />} />
            </Routes>
            <ToastContainer />
        </div>
    );
};

const enhance = compose(withAuthentication, withNotificationProvider, withPostProvider);

export default hot(module)(enhance(App));
